// src/components/OrderCard.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order, OrderItem } from '../types';

interface OrderCardProps {
    order: Order;
}


const getStatusConfig = (status: Order['status']) => {
    switch (status) {
        case 'pending':
            return { label: 'Заказ ожидает подтверждения', icon: '🕐', statusClass: 'status-pending' };
        case 'confirmed':
            return { label: 'Заказ подвержден', icon: '📦', statusClass: 'status-confirmed' };

        case 'completed':
            return { label: 'Заказ завершен', icon: '✅', statusClass: 'status-completed' };
        case 'cancelled':
            return { label: 'Заказа отменен', icon: '❌', statusClass: 'status-cancelled' };
        default:
            return { label: 'Неизвестно', icon: '🕐', statusClass: 'status-unknown' };
    }
};

// Функция для статуса единицы товара
// Функция для статуса единицы товара (только новые статусы)
const getItemStatusConfig = (status?: string) => {
    switch (status) {
        // Основные статусы
        case 'processing':
            return { label: 'Заказ в обработке', icon: '🕐', class: 'item-status-processing' };
        case 'sent_to_assembly':
            return { label: 'Отправлен на сборку', icon: '📋', class: 'item-status-sent-to-assembly' };
        case 'assembled':
            return { label: 'Собран на складе', icon: '✅', class: 'item-status-assembled' };
        case 'in_transit':
            return { label: 'В пути', icon: '🚚', class: 'item-status-in-transit' };
        case 'with_courier':
            return { label: 'Заказ у курьера', icon: '🛵', class: 'item-status-with-courier' };
        case 'delivered':
            return { label: 'Доставлен', icon: '🏠', class: 'item-status-delivered' };
        case 'ready_for_pickup':
            return { label: 'Готов к получению', icon: '🏪', class: 'item-status-ready-for-pickup' };
        case 'purchased':
            return { label: 'Куплен', icon: '💰', class: 'item-status-purchased' };

        // Возврат до покупки
        case 'return_refusal':
            return { label: 'Возврат: Отказ от покупки', icon: '↩️', class: 'item-status-return-refusal' };
        case 'return_defective':
            return { label: 'Возврат: Брак', icon: '⚠️', class: 'item-status-return-defective' };
        case 'return_wrong_product':
            return { label: 'Возврат: Другой товар', icon: '🔄', class: 'item-status-return-wrong' };
        case 'return_damaged':
            return { label: 'Возврат: Дефект', icon: '🔧', class: 'item-status-return-damaged' };
        case 'return_other':
            return { label: 'Возврат: Другая причина', icon: '📝', class: 'item-status-return-other' };

        // Возврат после покупки
        case 'return_after_defective':
            return { label: 'Возврат после покупки: Брак', icon: '⚠️', class: 'item-status-return-after-defective' };
        case 'return_after_refusal':
            return { label: 'Возврат после покупки: Отказ от покупки', icon: '↩️', class: 'item-status-return-after-refusal' };
        case 'return_after_damaged':
            return { label: 'Возврат после покупки: Дефект', icon: '🔧', class: 'item-status-return-after-damaged' };

        // Отмена
        case 'cancelled':
            return { label: 'Отменен', icon: '❌', class: 'item-status-cancelled' };

        default:
            return { label: 'Заказ в обработке', icon: '🕐', class: 'item-status-processing' };
    }
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

export const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const statusConfig = getStatusConfig(order.status);

    const deliveryMethodText = order.delivery_method === 'courier' ? 'Курьером' : 'Самовывоз';
    const paymentMethodText = order.payment_method === 'cash' ? 'Наличными' :
        order.payment_method === 'card' ? 'Картой' : 'Онлайн';

    // Функция для перехода к товару с выбранным вариантом
    const handleItemClick = (item: OrderItem) => {
        // Переходим на страницу товара, передавая ID варианта в state
        navigate(`/product/${item.product_id}`, {
            state: { selectedVariantId: item.variant_id }
        });
    };

    // Показываем все единицы или только первые 4
    const displayItems = expanded ? order.items : order.items?.slice(0, 4);

    return (
        <div className="order-card">
            {/* Шапка заказа */}
            <div className="order-header">
                <div className="order-header-top">
                    <div className="order-number">
                        Заказ № <span>{order.order_number}</span>
                    </div>
                    <div className={`order-status ${statusConfig.statusClass}`}>
                        <span>{statusConfig.icon}</span>
                        <span>{statusConfig.label}</span>
                    </div>
                    <button onClick={() => setExpanded(!expanded)} className="expand-btn">
                        {expanded ? '−' : '+'}
                    </button>
                </div>
                <div className="order-date">
                    <span>📅 Дата заказа: {formatDate(order.created_at)}</span>
                    <span>🚚 Способ получения: {deliveryMethodText}</span>
                </div>
            </div>

            {/* Товары в заказе - КАЖДАЯ ЕДИНИЦА ОТДЕЛЬНО */}
            <div className="order-items">
                {displayItems?.map((item, index) => (
                    <div
                        key={item.id}
                        className="order-item"
                        onClick={() => handleItemClick(item)}
                        style={{ cursor: 'pointer' }}
                    >
                        <img
                            src={item.product_image || 'https://via.placeholder.com/80'}
                            alt={item.product_name}
                            className="order-item-image"
                        />
                        <div className="order-item-info">
                            <div className="item-details">
                                <div className="item-name">{item.product_name}</div>
                                {item.variant_name && (
                                    <div className="item-variant">{item.variant_name}</div>
                                )}
                                {/* Уникальный код для каждой единицы */}
                                <div className="item-instance-id">
                                    <span></span>
                                    <span className="instance-code">№{item.instance_id ||  '---'}</span>
                                </div>
                                <div className="item-status">
                                    <span className={`status-badge ${getItemStatusConfig(item.status).class}`}>
                                        {getItemStatusConfig(item.status).icon} {getItemStatusConfig(item.status).label}
                                    </span>
                                </div>
                            </div>
                            <div className="item-price">
                                <div className="price-amount">{item.price_at_time.toLocaleString()} TJS</div>
                                <div className="item-total">
                                    Итого: {item.total.toLocaleString()} TJS
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {order.items && order.items.length > 4 && !expanded && (
                    <button onClick={() => setExpanded(true)} className="show-more-btn">
                        Показать еще {order.items.length - 4} товара
                    </button>
                )}
            </div>

            {/* Информация о доставке и оплате */}
            <div className="order-footer">
                <div className="footer-grid">
                    <div className="footer-block">
                        <p>Доставка</p>
                        <p>{deliveryMethodText}</p>
                        <p>{order.delivery_address}</p>
                        {order.delivery_price > 0 ? (
                            <p>Стоимость: {order.delivery_price} TJS</p>
                        ) : (
                            <p className="free-delivery">Бесплатная доставка</p>
                        )}
                    </div>
                    <div className="footer-block">
                        <p>Оплата</p>
                        <p>{paymentMethodText}</p>
                        <p>Товаров: {order.items?.length || 0} шт.</p>
                        <p>Сумма товаров: {order.items_total?.toLocaleString()} TJS</p>
                    </div>
                    <div className="footer-block total-block">
                        <p>Итого к оплате</p>
                        <p className="total-amount">{order.total_amount.toLocaleString()} TJS</p>
                        {order.notes && (
                            <p className="order-note">Комментарий: {order.notes}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};